/**
 * One delivery: what it says, and what may still be done about it.
 *
 * A courier records a job when it is booked and finishes it when it is done, so
 * the record has to be reachable in between — to add the rider, correct a fee
 * that was quoted wrong, or cancel it when the customer changes their mind.
 * Until this screen existed a delivery could be created and then only looked at,
 * which is half a workflow.
 *
 * What may be done depends on what the delivery has already become:
 *
 *   **Still running** — Pending, Assigned, In Transit, and no money recognised.
 *   Editable and cancellable by anyone who records deliveries here.
 *
 *   **Money recognised** — a sale exists. Correcting it now moves revenue and a
 *   rider's earnings, which is a different authority from recording a job, so
 *   it asks for `sales:void`. A courier does not have it; an owner does.
 *
 *   **Recorded elsewhere** — mirrored from dispatch. Corrected where it was
 *   made, because editing it here would put the two out of step until the next
 *   event silently undid the change.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Delivery } from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { date, money } from '../../lib/format';
import { Badge, Button, Card, ErrorState, Input, Spinner } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { DeliveryEntryForm } from './DeliveryEntryForm';
import { ControlBadges } from './ControlBadges';
import { PaymentProofSection } from './PaymentProofSection';
import { PayoutVerificationSection } from './PayoutVerificationSection';
import { FeeReviewSection } from './FeeReviewSection';

export function DeliveryDetailPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { business, can, user } = useAuth();
  const path = useBusinessPath();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currency = business?.currencyCode ?? 'NGN';
  const timezone = business?.timezone ?? 'UTC';

  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reason, setReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['delivery', id],
    queryFn: () => api.get<Delivery>(path(`/deliveries/${id}`)),
    enabled: Boolean(business && id),
  });

  const cancel = useMutation({
    mutationFn: () =>
      api.patch(path(`/deliveries/${id}`), {
        deliveryStatus: 'Cancelled',
        cancelReason: reason.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery', id] });
      void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      setCancelling(false);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not cancel this delivery.'),
  });

  /**
   * Delete, which is not cancel.
   *
   * Cancelling says the job did not happen and keeps it on the books saying so.
   * Deleting says it should never have been on the books at all — a duplicate,
   * or a row entered against the wrong customer. Its money is reversed and it
   * leaves every list and every total, so it navigates away rather than
   * re-rendering a record that no longer exists.
   */
  const remove = useMutation({
    mutationFn: () =>
      api.del(path(`/deliveries/${id}`), deleteReason.trim() ? { reason: deleteReason.trim() } : {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      navigate('/deliveries');
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not delete this delivery.'),
  });


  if (query.isPending) return <Spinner label="Loading delivery" />;
  if (query.isError || !query.data) {
    return <ErrorState message="That delivery could not be opened." />;
  }

  const d = query.data;
  const posted = Boolean(d.saleId);
  const isCancelled = ['Cancelled', 'Failed/Returned'].includes(d.deliveryStatus ?? '');
  const ours = d.originSource === 'manual';

  /**
   * Who may still change this.
   *
   * Recording a job and reversing money are different authorities. Once a sale
   * exists, changing the delivery moves revenue and a rider's earnings, so it
   * asks for the permission that already means exactly that.
   */
  /*
    Mirrors `assertMayAmendDelivery` on the API, which is what actually
    enforces this. Repeated here so a counter is not offered a button that
    will refuse it — the screen is the courtesy, the API is the boundary.
  */
  const counterRole = business?.role === 'location_operator' || business?.role === 'worker';
  const mine = d.recordedByUserId !== null && d.recordedByUserId === user?.id;
  const isPaid = (d.paymentStatus ?? '').trim().toLowerCase() === 'paid';

  const mayEdit =
    ours &&
    !isCancelled &&
    can('deliveries:manage') &&
    (!posted || can('sales:void')) &&
    // A counter corrects its own work, not somebody else's.
    (!counterRole || mine);

  /*
    Deleting, for an owner, does not depend on whether the delivery is still
    running or already posted: removing a row that should not exist is the same
    decision either way, and the posted money is reversed for them.

    A counter may delete only its own, and only while it is unpaid. A paid
    delivery is the owner's alone — not an admin's — because writing off money
    a customer handed over is a different decision from removing a typo.
  */
  const mayDelete =
    ours &&
    (isPaid
      ? // Money that arrived: the owner alone, whoever recorded it.
        can('deliveries:delete_paid')
      : can('deliveries:delete') || (can('deliveries:manage') && mine));

  const rows: Array<[string, string]> = [
    ['Reference', d.externalRef],
    // The customer's own order number (a WhatsApp CENO-… code) when it is not the reference.
    ...(d.dispatchRef && d.dispatchRef !== d.externalRef
      ? ([['Order number', d.dispatchRef]] as Array<[string, string]>)
      : []),
    ['Location', d.branchName ?? '—'],
    ['Service', d.serviceType ?? '—'],
    ['Delivery type', d.deliveryType === 'fast' ? 'Fast' : d.deliveryType === 'base' ? 'Base' : 'Not stated'],
    ...(d.zoneName ? ([['Zone', d.zoneName]] as Array<[string, string]>) : []),
    // "Sender", not "Customer": the column is `customer_*` for compatibility,
    // but for a courier it has always been the person at the pickup end.
    ['Sender', [d.customerName, d.customerPhone].filter(Boolean).join(' · ') || '—'],
    // Em dash on anything recorded before receivers existed, or imported.
    ['Receiver', [d.receiverName, d.receiverPhone].filter(Boolean).join(' · ') || '—'],
    ['Pick-up', d.pickup ?? '—'],
    ['Drop-off', d.destination ?? '—'],
    ['Paid by', d.paidBy === 'sender' ? 'Sender' : d.paidBy === 'receiver' ? 'Receiver' : '—'],
    ['Rider', [d.riderExternalId, d.riderName, d.riderPhone].filter(Boolean).join(' · ') || '—'],
    ['Fee', money(d.fee, currency)],
    ['Rider payout', money(d.riderPayout, currency)],
    ['Business keeps', money(d.companyShare, currency)],
    ['How they paid', d.paymentMethod ?? '—'],
    // No longer asked for; still shown on the records that carry them.
    ...(d.distanceKm ? ([['Distance', `${d.distanceKm} km`]] as Array<[string, string]>) : []),
    ...(d.fuelCost ? ([['Fuel / running cost', money(d.fuelCost, currency)]] as Array<[string, string]>) : []),
    ['Dispatch job number', d.dispatchRef ?? '—'],
    ['Notes', d.notes ?? '—'],
    ['Recorded by', d.recordedByName ?? '—'],
    ['Recorded', date(d.createdAt, timezone)],
  ];

  /*
    Payment Proof sits directly under the payment rows, so the screenshot is
    read beside the claim it supports rather than at the foot of the page.
  */
  const paymentEnds = rows.findIndex(([label]) => label === 'How they paid') + 1;
  const rowGroups = [rows.slice(0, paymentEnds), rows.slice(paymentEnds)];
  const renderRows = (group: Array<[string, string]>): ReactNode =>
    group.map(([label, value]) => (
      <div key={label} className="flex flex-wrap gap-x-4 gap-y-0.5 px-4 py-2.5">
        <dt className="w-40 shrink-0 text-xs text-slate-500">{label}</dt>
        <dd className="min-w-0 flex-1 text-sm text-slate-900">{value}</dd>
      </div>
    ));

  if (editing) {
    return (
      <>
        <PageHeader title="Correct this delivery" description={d.externalRef} />
        <Card>
          <div>
            <DeliveryEntryForm
              branchId={d.branchId}
              existing={{
                id: d.id,
                serviceType: d.serviceType,
                customerName: d.customerName,
                customerPhone: d.customerPhone,
                receiverName: d.receiverName,
                receiverPhone: d.receiverPhone,
                paidBy: d.paidBy,
                pickup: d.pickup,
                destination: d.destination,
                fee: d.fee,
                riderName: d.riderName,
                riderPhone: d.riderPhone,
                riderExternalId: d.riderExternalId,
                riderId: d.riderId,
                paymentMethod: d.paymentMethod,
                paymentStatus: d.paymentStatus,
                deliveryStatus: d.deliveryStatus,
                notes: d.notes,
                cancelReason: d.cancelReason,
                dispatchRef: d.dispatchRef,
                deliveryType: d.deliveryType ?? null,
                zoneId: d.zoneId,
                zoneName: d.zoneName,
                hasProof: Boolean(d.paymentProofAttachmentId),
              }}
              onDone={() => setEditing(false)}
              onCancel={() => setEditing(false)}
            />
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={d.externalRef}
        description={`${d.pickup ?? ''}${d.pickup && d.destination ? ' → ' : ''}${d.destination ?? ''}`}
      />

      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-4">
          <Badge tone={isCancelled ? 'danger' : 'success'}>{d.deliveryStatus ?? 'Unknown'}</Badge>
          <Badge tone={d.paymentStatus === 'Paid' ? 'success' : 'neutral'}>
            {d.paymentStatus ?? '—'}
          </Badge>
          {posted && <Badge tone="info">Counted in the books</Badge>}
          {!ours && <Badge tone="warning">Recorded in dispatch</Badge>}
          <ControlBadges delivery={d} />
        </div>

        <FeeReviewSection delivery={d} timezone={timezone} />

        <dl className="divide-y divide-slate-100">{renderRows(rowGroups[0]!)}</dl>

        <PaymentProofSection delivery={d} timezone={timezone} />

        <PayoutVerificationSection delivery={d} timezone={timezone} />

        <dl className="divide-y divide-slate-100">
          {renderRows(rowGroups[1]!)}
          {d.supersededByDeliveryId && (
            <div className="flex flex-wrap gap-x-4 px-4 py-2.5">
              <dt className="w-40 shrink-0 text-xs text-slate-500">Duplicate of</dt>
              <dd className="min-w-0 flex-1 text-sm">
                <Link
                  to={`/deliveries/${d.supersededByDeliveryId}`}
                  className="text-indigo-700 hover:underline"
                >
                  The record that is kept
                </Link>
              </dd>
            </div>
          )}
          {isCancelled && d.cancelReason && (
            <div className="flex flex-wrap gap-x-4 px-4 py-2.5">
              <dt className="w-40 shrink-0 text-xs text-slate-500">Cancelled because</dt>
              <dd className="min-w-0 flex-1 text-sm text-slate-900">{d.cancelReason}</dd>
            </div>
          )}
        </dl>

        <div className="flex flex-wrap gap-2 border-t border-slate-100 p-4">
          <Button variant="secondary" onClick={() => navigate('/deliveries')}>
            Back
          </Button>

          {mayEdit && (
            <>
              <Button onClick={() => setEditing(true)}>Edit</Button>
              <Button variant="ghost" onClick={() => setCancelling(true)}>
                Cancel delivery
              </Button>
            </>
          )}

          {mayDelete && (
            <Button variant="ghost" className="text-red-700" onClick={() => setDeleting(true)}>
              Delete
            </Button>
          )}
        </div>

        {/*
          Said rather than hidden. A courier who cannot change something should
          know why, otherwise the screen simply looks broken to them.
        */}
        {!mayEdit && !isCancelled && (
          <p className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
            {!ours
              ? 'This delivery was recorded in the dispatch system and is corrected there.'
              : posted
                ? 'This delivery has been counted in the books. Changing it now moves revenue and the rider’s earnings, so an owner has to do it.'
                : 'You do not have permission to change this delivery.'}
          </p>
        )}
        {isCancelled && (
          <p className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
            Cancelled deliveries are kept, with their history, so what happened stays answerable.
          </p>
        )}
      </Card>

      {deleting && (
        <Card className="mt-4">
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-slate-900">Delete this delivery?</h2>
            {error && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <p className="text-sm text-slate-700">
              It will be removed from the delivery list and from every total. Any revenue and rider
              payout it posted are reversed. This is not the same as cancelling: cancel keeps the job
              on the books saying it did not happen.
            </p>
            <Input
              label="Why is it being deleted? (optional)"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Entered twice by mistake"
            />
            <p className="text-xs text-slate-500">
              A record of the deletion is kept, with who did it and when.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => remove.mutate()} loading={remove.isPending}>
                Yes, delete it
              </Button>
              <Button variant="ghost" onClick={() => setDeleting(false)}>
                Keep it
              </Button>
            </div>
          </div>
        </Card>
      )}

      {cancelling && (
        <Card className="mt-4">
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-slate-900">Cancel this delivery</h2>
            {error && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <Input
              label="Why is it cancelled?"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Customer changed their mind"
            />
            <p className="text-xs text-slate-500">
              The record is kept either way — “cancelled” on its own answers nothing at month end.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => cancel.mutate()}
                loading={cancel.isPending}
                disabled={reason.trim().length === 0}
              >
                Cancel delivery
              </Button>
              <Button variant="ghost" onClick={() => setCancelling(false)}>
                Keep it
              </Button>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}
